import { FileText, Sparkles, Star, Video } from "lucide-react";
import JobHighlightPills from "./JobHighlightPills";
import { getJobRequirementsHtml, getJobResponsibilitiesHtml } from "../pages/applicantData";
import PitchVideoPlayer from "./PitchVideoPlayer";

function DetailSectionHead({ icon: Icon, children, as: Tag = "h3" }) {
  return (
    <Tag className="nd1-job-detail__section-head">
      <span className="nd1-job-detail__section-icon" aria-hidden="true">
        <Icon className="nd1-icon" size={20} strokeWidth={2} />
      </span>
      {children}
    </Tag>
  );
}

/**
 * Standard job advertising body: highlight pills, role summary, must-haves, job description,
 * hiring manager pitch. Use everywhere a full listing is shown.
 *
 * Compensation narrative belongs in the job description (or a future poster-supplied field),
 * not a separate synthesized line from structured salary data.
 *
 * @param {"detail"|"panel"} layout — `detail`: h3 section titles. `panel`: h4 with same `job-section-title` / `job-section-subtitle` classes (search tab) to match full page font sizes.
 * @param {boolean} showPitch — set false to hide the pitch block (rare).
 * @param {boolean} showMetaCards — set false when meta cards render in JobDetailHeader.
 */
export default function JobListingContent({ job, layout = "detail", showPitch = true, showMetaCards = true }) {
  if (!job) return null;

  const isPanel = layout === "panel";
  const isDetail = layout === "detail";
  const MainHeading = isPanel ? "h4" : "h3";
  /* Tab view uses h4 for outline, but same class as detail so typography matches full job page */
  const mainHeadingClass = "job-section-title";
  const PitchHeading = isPanel ? "h4" : "h3";
  const pitchHeadingClass = isPanel ? "job-section-subtitle" : "job-section-title";

  const pitchSrc = job.pitchVideoUrl || job.hiringManagerPitchVideoUrl || "";

  const requirementsBlock = (
    <>
      {isDetail ? (
        <DetailSectionHead icon={Star}>Must-have Skills and Experience</DetailSectionHead>
      ) : (
        <MainHeading className={mainHeadingClass}>Must-have Skills and Experience</MainHeading>
      )}
      <div
        className={`job-body-html${isDetail ? " nd1-job-detail__checklist" : ""}`}
        dangerouslySetInnerHTML={{ __html: getJobRequirementsHtml(job) }}
      />
    </>
  );

  const descriptionBlock = (
    <>
      {isDetail ? (
        <DetailSectionHead icon={FileText}>Job description</DetailSectionHead>
      ) : (
        <MainHeading className={mainHeadingClass}>Job description</MainHeading>
      )}
      <div
        className={`job-body-html${isDetail ? " nd1-job-detail__checklist nd1-job-detail__checklist--columns" : ""}`}
        dangerouslySetInnerHTML={{ __html: getJobResponsibilitiesHtml(job) }}
      />
    </>
  );

  const pitchBlock = showPitch ? (
    <section className={isDetail ? "nd1-job-detail__section nd1-job-detail__section--pitch" : undefined}>
      {isDetail ? (
        <DetailSectionHead icon={Video} as={PitchHeading}>
          Hiring manager pitch
        </DetailSectionHead>
      ) : (
        <PitchHeading className={pitchHeadingClass}>Hiring manager pitch</PitchHeading>
      )}
      {pitchSrc ? (
        <PitchVideoPlayer
          src={pitchSrc}
          className="job-pitch-video"
          ariaLabel="Hiring manager pitch video preview"
        />
      ) : (
        <div className="job-pitch-empty" role="status" aria-live="polite">
          <svg
            className="job-pitch-empty__icon"
            width="48"
            height="48"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M9 16c.85-.63 1.885-1 3-1s2.15.37 3 1m-5.5-5.5V10m5 .5V10" />
            <path d="M3 9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h5.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C21 6.04 21 7.16 21 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 21 16.84 21 14.6 21H9.4c-2.24 0-3.36-.436-4.216-1.748A4 4 0 0 1 3 14.6z" />
          </svg>
          <div className="job-pitch-empty__text">
            We are sorry, the hiring manager has not provided a pitch. We will ask them to do so next time.
          </div>
        </div>
      )}
    </section>
  ) : null;

  const body = (
    <>
      {showMetaCards ? <JobHighlightPills job={job} variant={isDetail ? "detail" : "default"} /> : null}

      {job.summary?.trim() ? (
        isDetail ? (
          <div className="nd1-job-detail__mission">
            <Sparkles className="nd1-job-detail__mission-icon nd1-icon" size={36} strokeWidth={1.75} aria-hidden="true" />
            <p className="job-role-summary">{job.summary.trim()}</p>
          </div>
        ) : (
          <p className="job-role-summary">{job.summary.trim()}</p>
        )
      ) : null}

      {isDetail ? (
        <section className="nd1-job-detail__section nd1-job-detail__section--requirements">{requirementsBlock}</section>
      ) : (
        requirementsBlock
      )}

      {isDetail ? (
        <section className="nd1-job-detail__section nd1-job-detail__section--description">{descriptionBlock}</section>
      ) : (
        descriptionBlock
      )}

      {pitchBlock}
    </>
  );

  if (isDetail) {
    return <div className="nd1-job-detail__listing">{body}</div>;
  }

  return body;
}
